import { useEffect, useMemo } from 'react';
import {
  getNewColumnsVisibilityState,
  readStoredColumnOrder,
  rememberColumns,
} from '../utils/column-visibility';

/**
 * Shows a column added to a management table by default, for users whose saved
 * column configuration predates it, while leaving columns they have since
 * hidden alone. `introducedColumnIds` names the columns that shipped before
 * this migration existed, so the first run can surface them without also
 * restoring everything else the user had hidden.
 *
 * Wraps the `initialColumnsOrder` / `forceOverwrite` pair a table already
 * hands to `useTableManageColumns` and returns the three values to pass
 * instead. Place it last, after any column-order hook, so nothing downstream
 * drops the force. `forceOverwrite` rewrites the stored column order too, so
 * when the restore is the only reason to force, the stored order is handed
 * back as `initialColumnsOrder` and that write changes nothing — a user's
 * drag survives the one-time restore.
 */
const useNewColumnsVisibleByDefault = ({
  name,
  headers,
  introducedColumnIds,
  initialColumnsOrder,
  forceOverwrite = false,
}: {
  name: string,
  headers: Array<Object>,
  introducedColumnIds?: Array<string>,
  initialColumnsOrder?: Object,
  forceOverwrite?: boolean,
}) => {
  const state = useMemo(() => {
    const { initialSelectedColumns, shouldForceOverwrite } =
      getNewColumnsVisibilityState({ headers, name, introducedColumnIds });

    if (!shouldForceOverwrite) {
      return {
        initialColumnsOrder,
        initialSelectedColumns: undefined,
        shouldForceOverwrite: forceOverwrite,
      };
    }

    return {
      initialColumnsOrder: forceOverwrite
        ? initialColumnsOrder
        : readStoredColumnOrder(name) ?? initialColumnsOrder,
      initialSelectedColumns,
      shouldForceOverwrite: true,
    };
  }, [headers, name, introducedColumnIds, initialColumnsOrder, forceOverwrite]);

  // Recorded after render, once the state above has been computed from the
  // previous known list; a re-render then finds nothing new and stops forcing.
  useEffect(() => {
    rememberColumns({ headers, name });
  }, [headers, name]);

  return state;
};

export default useNewColumnsVisibleByDefault;
